import { injectable, inject, LazyServiceIdentifier } from 'inversify';

import { AuthService } from './auth.service';
import { RefreshService } from './refresh.service';
import { accessTokenExpSeconds } from '../utils/token-expiry.util';
import { isAnonymousToken, isAccessTokenStale } from '../utils/token-expiry.util';
import { serverNowSeconds } from '../utils/clock.util';

/** Maximum access-token TTL this scheduler is willing to wait (30 min in ms). */
const ACCESS_TTL_MS = 30 * 60 * 1000;

/** Tokens expiring farther than this into the future are legacy long-lived bearers — skip them. */
const LEGACY_THRESHOLD_SECONDS = 3600;

/** Jitter factor: ±10% of the computed delay. */
const JITTER_FACTOR = 0.1;

function jitter(delayMs: number): number {
  const range = delayMs * JITTER_FACTOR;
  return delayMs + (Math.random() * 2 - 1) * range;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

@injectable()
export class ProactiveRefreshScheduler {
  private timer: any = null;
  private listenersRegistered = false;

  private boundOnWake = () => this.onWake();

  constructor(
    // Deferred: scheduler → refresh.service → login.service → (login imports
    // scheduler) is a cycle; defer the class reference to plan time to avoid a
    // module-load TDZ in the webpack build.
    @inject(new LazyServiceIdentifier(() => RefreshService)) private refreshService: RefreshService,
    @inject(AuthService) private authService: AuthService,
  ) {}

  start(): void {
    // FD4: cookies disabled — do not schedule.
    if (this.refreshService.cookieSessionUnavailable) return;

    const token = this.authService.getToken();
    // No token or anonymous token — do not schedule.
    if (!token || isAnonymousToken(token)) return;

    const exp = accessTokenExpSeconds(token);
    if (exp === null) return;

    const secondsLeft = exp - serverNowSeconds();

    // Legacy long-lived bearer (e.g. 365-day token) — skip scheduling.
    if (secondsLeft >= LEGACY_THRESHOLD_SECONDS) return;

    const rawDelay = secondsLeft * 1000 * 0.75;
    const delay = clamp(jitter(rawDelay), 0, ACCESS_TTL_MS);

    this.stop();
    this.timer = setTimeout(() => {
      this.refreshService.refreshAcrossTabs().subscribe({
        next: () => this.start(),
        error: () => {},
      });
    }, delay);

    // Register focus/online wake listeners once (SSR-guarded).
    if (typeof window !== 'undefined' && !this.listenersRegistered) {
      window.addEventListener('focus', this.boundOnWake);
      window.addEventListener('online', this.boundOnWake);
      this.listenersRegistered = true;
    }
  }

  private onWake(): void {
    if (this.refreshService.cookieSessionUnavailable) return;
    const token = this.authService.getToken();
    if (!token || isAnonymousToken(token)) return;

    // Only spend a network call if the token is actually stale; otherwise just
    // re-arm the timer. Without this, a browser lacking Web Locks (where
    // refreshAcrossTabs() can't no-op via the lock-body staleness recheck) would
    // POST /auth/refresh on EVERY window focus/online event regardless of freshness.
    if (!isAccessTokenStale(token)) {
      this.start();
      return;
    }

    this.refreshService.refreshAcrossTabs().subscribe({
      next: () => this.start(),
      error: () => {},
    });
  }

  stop(): void {
    clearTimeout(this.timer);
    this.timer = null;

    // Remove focus/online listeners (SSR-guarded).
    if (typeof window !== 'undefined' && this.listenersRegistered) {
      window.removeEventListener('focus', this.boundOnWake);
      window.removeEventListener('online', this.boundOnWake);
      this.listenersRegistered = false;
    }
  }
}
